import { useState, useEffect } from "react";
import { cdnUrl } from "commons/cdn";
import useSWR from "swr";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Search } from "lucide-react";
import { Template } from "types";
import { useDebounce } from "hooks/useDebounce";
import MyPagination from "components/ui/MyPagination";

interface TemplatePickerProps {
  children?: React.ReactNode;
  onSelect: (template: Template) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function TemplatePicker({
  children,
  onSelect,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: TemplatePickerProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selected, setSelected] = useState<Template | null>(null);
  const [after, setAfter] = useState<string | null>(null);
  const [afters, setAfters] = useState<string[]>([]);
  const [searchTid, setSearchTid] = useState("");
  const [debouncedSearchTid, setDebouncedSearchTid] = useState("");

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  // 使用防抖 hook：搜索输入 500ms 后才更新防抖值
  const updateDebouncedSearch = useDebounce((value: string) => {
    setDebouncedSearchTid(value);
    setAfter(null);
    setAfters([]);
  }, 500);

  // 监听搜索输入变化
  useEffect(() => {
    updateDebouncedSearch(searchTid);
  }, [searchTid, updateDebouncedSearch]);

  // 获取 template 列表
  const {
    data: templates = [],
    isLoading,
    mutate,
  } = useSWR<Template[]>(
    open
      ? [
          "/templates",
          {
            ...(after && { after }),
            ...(debouncedSearchTid && { tid: debouncedSearchTid }),
            limit: 20, // 每页 20 条
          },
        ]
      : null
  );

  const nextAfter = templates && templates.length === 20 ? templates[templates.length - 1]?.id : undefined;

  const handleSubmit = () => {
    if (selected) {
      onSelect(selected);
      handleOpenChange(false);
    }
  };

  const goAfter = (nextAfter: string | null) => {
    if (nextAfter === null) {
      setAfter(null);
      setAfters([]);
    } else {
      const index = afters.findIndex((a) => a === nextAfter);
      if (index === -1) {
        setAfter(nextAfter);
        setAfters([...afters, nextAfter]);
      } else {
        setAfter(nextAfter);
      }
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setAfter(null);
      setAfters([]);
      setSelected(null);
      setSearchTid("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>选择模板</DialogTitle>
          <DialogDescription className="hidden" />
        </DialogHeader>

        <div className="flex items-center">
          <MyPagination
            nextAfter={nextAfter}
            after={after}
            goAfter={goAfter}
            afters={afters}
            className="w-auto m-0"
          />

          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="搜索模板 ID..."
              value={searchTid}
              onChange={(e) => setSearchTid(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        {/* Template 列表 */}
        <ScrollArea className="flex-1 max-h-[50vh]">
          {isLoading ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              加载中...
            </div>
          ) : templates.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              暂无数据
            </div>
          ) : (
            <div className="space-y-2 p-2">
              {templates.map((template: Template) => (
                <TemplateItem
                  key={template.id}
                  template={template}
                  selected={selected?.id === template.id}
                  onClick={() => setSelected(template)}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* 分页控制 */}


        {/* 底部操作栏 */}
        <DialogFooter className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {selected ? (
              <>
                已选择: <span className="font-medium">{selected.name || selected.id}</span>
              </>
            ) : (
              "请选择一个模板"
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              取消
            </Button>
            <Button onClick={handleSubmit} disabled={!selected}>
              确定
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface TemplateItemProps {
  template: Template;
  selected: boolean;
  onClick: () => void;
}

function TemplateItem({ template, selected, onClick }: TemplateItemProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all",
        "hover:border-primary hover:shadow-md",
        selected
          ? "border-primary bg-primary/10 ring-2 ring-primary/30"
          : "border-transparent bg-muted/30"
      )}
      onClick={onClick}
    >
      {/* 预览图 */}
      {template.preview && (
        <div className="w-20 h-20 flex-shrink-0 rounded overflow-hidden bg-muted">
          <img
            src={cdnUrl(template.preview)}
            alt={template.name || "模板预览"}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* 信息 */}
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{template.name || "未命名"}</div>
        {template.intro && (
          <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
            {template.intro}
          </div>
        )}
        <div className="text-xs text-muted-foreground mt-1 flex gap-2">
          <span>ID: {template.id}</span>
          {template.publisher && <span>创建者: {template.publisher}</span>}
        </div>
      </div>
    </div>
  );
}
